import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import {
  ExecOrgAutocomplete,
  type UsFilters,
  UsTableFilters,
  useExecOrgParentLuts,
  useExecRoleLuts,
  useUsRoleTypeLuts
} from 'components'
import { type State, UsStateSelect } from 'components/us/common/state-select'
import { type Role } from 'services'
import {
  type ExecOrgDescendantItem,
  type ExecOrgParentLutItem,
  getExecutiveOrgDescendants
} from 'services/executives'
import { FEDERAL_EXECUTIVE_ROLES, STATE_EXECUTIVE_ROLES } from 'utils'

const FED_EXEC_ROLE_IDS = new Set<number>(FEDERAL_EXECUTIVE_ROLES)
const STATE_EXEC_ROLE_IDS = new Set<number>(STATE_EXECUTIVE_ROLES)
const STATE_EXECUTIVES_SYNTHETIC_LABEL = 'State Executives'
const STATE_EXECUTIVES_OPTION_KEY = 'state-executives'

export type GeneralRoleSelection =
  | { kind: 'role', role: Role }
  | { kind: 'state-executives' }

const isStateExecutivesBranch = (
  selection: GeneralRoleSelection | null
): boolean => selection?.kind === 'state-executives'

const isFederalRole = (role: Role): boolean => FED_EXEC_ROLE_IDS.has(role.id)
const isStateExecRole = (role: Role): boolean => STATE_EXEC_ROLE_IDS.has(role.id)
const isExecutiveRoleAllowedInDropdown = (role: Role): boolean => (
  isFederalRole(role) || isStateExecRole(role)
)

export interface ExecutivePositionSearchFilters extends UsFilters {
  generalRole: GeneralRoleSelection | null
  state: State | null
  parentOrg: ExecOrgParentLutItem | null
  childOrg: ExecOrgDescendantItem | null
  execRoleId: number | null
  vacantOnly: boolean
  includeSubOffices: boolean
}

export const initialExecutivePositionFilters: ExecutivePositionSearchFilters = {
  generalRole: null,
  state: null,
  parentOrg: null,
  childOrg: null,
  execRoleId: null,
  vacantOnly: false,
  includeSubOffices: false,
  page: 1,
  order_field: 'last_name',
  order_direction: 'ASC'
}

interface Props {
  filters: ExecutivePositionSearchFilters
  updateFilters: (filters: any) => void
  doFilter: () => Promise<void>
  cleanFilters: () => void
}

export const ExecutivePositionFilters: React.FC<Props> = ({
  filters,
  updateFilters,
  doFilter,
  cleanFilters
}) => {
  const { lutValues: roleLut } = useUsRoleTypeLuts()
  const { lutValues: parentLut } = useExecOrgParentLuts()
  const { lutValues: execRoleLut } = useExecRoleLuts()

  const generalRoleOptions = useMemo<GeneralRoleSelection[]>(() => {
    const roles = Array.from(roleLut.roles.values())
      .filter(isExecutiveRoleAllowedInDropdown)
      .sort((a, b) => a.desc.localeCompare(b.desc))
    const wrapped: GeneralRoleSelection[] = roles.map(role => ({ kind: 'role', role }))
    return [{ kind: 'state-executives' }, ...wrapped]
  }, [roleLut.roles])

  const generalRoleLabel = (selection: GeneralRoleSelection): string => (
    isStateExecutivesBranch(selection)
      ? STATE_EXECUTIVES_SYNTHETIC_LABEL
      : (selection as { kind: 'role', role: Role }).role.desc
  )

  const generalRoleKey = (selection: GeneralRoleSelection): string => (
    isStateExecutivesBranch(selection)
      ? STATE_EXECUTIVES_OPTION_KEY
      : `role-${(selection as { kind: 'role', role: Role }).role.id}`
  )

  const isStateMode = filters.generalRole != null && (
    isStateExecutivesBranch(filters.generalRole) ||
    (filters.generalRole.kind === 'role' && isStateExecRole(filters.generalRole.role))
  )
  const isFederalMode = filters.generalRole != null &&
    filters.generalRole.kind === 'role' &&
    isFederalRole(filters.generalRole.role)
  const parentOrgEnabled = isFederalMode || (isStateMode && filters.state != null)
  const stateEnabled = isStateMode
  const childOrgEnabled = filters.parentOrg != null
  const execRoleEnabled = filters.generalRole != null && !isStateExecutivesBranch(filters.generalRole)

  const currentRoleId: number | null = filters.generalRole?.kind === 'role'
    ? filters.generalRole.role.id
    : null

  const parentOrgOptions = useMemo<ExecOrgParentLutItem[]>(() => {
    const all = Array.from(parentLut.parents.values())
    if (isStateExecutivesBranch(filters.generalRole)) {
      if (filters.state == null) return []
      return all
        .filter(p => p.state.id === filters.state?.id && STATE_EXEC_ROLE_IDS.has(p.role.id))
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    if (currentRoleId == null) return []
    return all.filter(parent => {
      if (parent.role.id !== currentRoleId) return false
      if (isStateMode && filters.state != null) {
        return parent.state.id === filters.state.id
      }
      return true
    }).sort((a, b) => a.name.localeCompare(b.name))
  }, [parentLut.parents, currentRoleId, isStateMode, filters.state, filters.generalRole])

  const execRoleOptions = useMemo(() => {
    if (currentRoleId == null) return []
    return execRoleLut.execRoles
      .filter(execRole => execRole.role.id === currentRoleId)
      .sort((a, b) => a.descShort.localeCompare(b.descShort))
  }, [execRoleLut.execRoles, currentRoleId])

  const [childOrgOptions, setChildOrgOptions] = useState<ExecOrgDescendantItem[]>([])
  const [childLoading, setChildLoading] = useState(false)

  useEffect(() => {
    if (filters.parentOrg == null) {
      setChildOrgOptions([])
      return
    }
    const parentId = filters.parentOrg.id
    setChildLoading(true)
    void getExecutiveOrgDescendants(parentId)
      .then(setChildOrgOptions)
      .catch(() => { setChildOrgOptions([]) })
      .finally(() => { setChildLoading(false) })
  }, [filters.parentOrg])

  return (
    <UsTableFilters
      filters={filters}
      updateFilters={updateFilters}
      doFilter={doFilter}
      cleanFilters={cleanFilters}
    >
      <Grid item xs={4}>
        <FormControl fullWidth>
          <InputLabel>General Role</InputLabel>
          <Select
            label="General Role"
            value={filters.generalRole != null ? generalRoleKey(filters.generalRole) : ''}
            onChange={(event) => {
              const value = event.target.value
              if (value === '') {
                updateFilters({
                  generalRole: null,
                  state: null,
                  parentOrg: null,
                  childOrg: null,
                  execRoleId: null
                })
                return
              }
              if (value === STATE_EXECUTIVES_OPTION_KEY) {
                updateFilters({
                  generalRole: { kind: 'state-executives' },
                  state: null,
                  parentOrg: null,
                  childOrg: null,
                  execRoleId: null
                })
                return
              }
              const id = Number(value.replace('role-', ''))
              const role = roleLut.roles.get(id)
              if (role != null) {
                updateFilters({
                  generalRole: { kind: 'role', role },
                  state: null,
                  parentOrg: null,
                  childOrg: null,
                  execRoleId: null
                })
              }
            }}
          >
            <MenuItem value="">None</MenuItem>
            {generalRoleOptions.map(option => (
              <MenuItem key={generalRoleKey(option)} value={generalRoleKey(option)}>
                {generalRoleLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <UsStateSelect
            value={filters.state}
            updateState={(state) => { updateFilters({ state, parentOrg: null, childOrg: null }) }}
            disabled={!stateEnabled}
            onlyUSStates={false}
          />
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <Autocomplete
            disabled={!parentOrgEnabled}
            options={parentOrgOptions}
            getOptionLabel={option => option.name ?? ''}
            value={filters.parentOrg}
            onChange={(_, newValue) => { updateFilters({ parentOrg: newValue, childOrg: null }) }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => <TextField {...params} label="Parent Office"/>}
          />
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <ExecOrgAutocomplete
            label="Child Office"
            disabled={!childOrgEnabled}
            loading={childLoading}
            options={childOrgOptions}
            value={filters.childOrg}
            onChange={(newValue) => { updateFilters({ childOrg: newValue }) }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <InputLabel>Executive Role</InputLabel>
          <Select
            label="Executive Role"
            disabled={!execRoleEnabled}
            value={filters.execRoleId ?? ''}
            onChange={(event) => {
              const v = event.target.value
              updateFilters({ execRoleId: v === '' ? null : Number(v) })
            }}
          >
            <MenuItem value="">None</MenuItem>
            {execRoleOptions.map(role => (
              <MenuItem key={role.id} value={role.id}>{role.descShort}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <FormControlLabel
            control={
              <Switch
                checked={filters.vacantOnly}
                onChange={(event) => { updateFilters({ vacantOnly: event.target.checked }) }}
              />
            }
            label={<Typography>Vacant only</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filters.includeSubOffices}
                disabled={filters.parentOrg == null}
                onChange={(event) => { updateFilters({ includeSubOffices: event.target.checked }) }}
              />
            }
            label={<Typography>Include all sub-office levels</Typography>}
          />
        </Stack>
      </Grid>
    </UsTableFilters>
  )
}
